import type { FunctionComponent } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { FFFErrorBoundary } from '@fff-web/fff-ui-shared';
import AnalyticsWrapper from './AnalyticsWrapper';
import { addPathPrefix } from './helpers';
import ContactUs from './pages/ContactUs';
import EmailPreferences from './pages/EmailPreferences';
import { LockInRatePage } from './pages/LockInRate';
import MyAccountContainer from './pages/MyAccount';
import NotFoundError from './pages/NotFoundError';
import ProRatedUpgradePage from './pages/ProRatedUpgrade';
import ReactivateMembership from './pages/ReactivateMembership';
import ReactivateThanks from './pages/ReactivateThanks';
import ReactivateToday from './pages/ReactivateToday';
import ShipmentTracking from './pages/ShipmentTracking';
import SubscribeToday from './pages/SubscribeToday';
import TrialUpgrade from './pages/TrialUpgrade';
import TrialUpgradeThanks from './pages/TrialUpgradeThanks';
import { UpgradeRoot } from './pages/UpgradeRedesign';
import UpgradeThanks from './pages/UpgradeThanks';
import {
  ACTIVATE_BOX_SUBSCRIPTION,
  ACTIVATE_MEMBERSHIP,
  CONTACT_US_PATH,
  EMAIL_PREFERENCES,
  LOCK_IN_RATE_PATH,
  MY_ACCOUNT,
  ORDER_TRACKING,
  PRORATE_UPGRADE,
  REACTIVATE_MEMBERSHIP,
  REACTIVATE_THANKS,
  REACTIVATE_TODAY,
  SUBSCRIBE_TODAY_THANKS,
  TRIAL_UPGRADE_THANKS,
  UPGRADE_CONFIRMATION,
  UPGRADE_THANKS,
} from './utils/routePaths';

type Props = {
  flags: {
    newMyAccount: string;
    newUpgrade: boolean;
    newReactivate: boolean;
  };
};

export const AppRoutes: FunctionComponent<Props> = ({ flags }) => {
  if (!flags || Object.keys(flags).length === 0) {
    return null;
  }

  return (
    <FFFErrorBoundary>
      <Router>
        <AnalyticsWrapper>
          <Switch>
            {flags.newReactivate !== false && [
              <Route
                exact
                path={addPathPrefix(REACTIVATE_TODAY, flags)}
                component={ReactivateToday}
                key="1"
              />,
              <Route
                exact
                path={addPathPrefix(REACTIVATE_MEMBERSHIP, flags)}
                component={ReactivateMembership}
                key="2"
              />,
              <Route
                exact
                path={addPathPrefix(REACTIVATE_THANKS, flags)}
                component={ReactivateThanks}
                key="3"
              />,
            ]}
            {flags.newUpgrade !== false && [
              <Route
                exact
                path={addPathPrefix(UPGRADE_CONFIRMATION, flags)}
                component={UpgradeRoot}
                key="4"
              />,
              <Route
                exact
                path={addPathPrefix(ACTIVATE_MEMBERSHIP, flags)}
                component={TrialUpgrade}
                key="5"
              />,
              <Route
                exact
                path={addPathPrefix(UPGRADE_THANKS, flags)}
                component={UpgradeThanks}
                key="6"
              />,
              <Route
                exact
                path={addPathPrefix(PRORATE_UPGRADE, flags)}
                component={ProRatedUpgradePage}
                key="7"
              />,
            ]}
            <Route exact path={ACTIVATE_BOX_SUBSCRIPTION} component={SubscribeToday} key="8" />
            <Route
              exact
              path={addPathPrefix(TRIAL_UPGRADE_THANKS, flags)}
              component={TrialUpgradeThanks}
              key="9"
            />
            <Route
              exact
              path={addPathPrefix(EMAIL_PREFERENCES, flags)}
              component={EmailPreferences}
              key="10"
            />
            <Route exact path={SUBSCRIBE_TODAY_THANKS} component={ReactivateThanks} key="11" />
            <Route
              path={addPathPrefix(`${ORDER_TRACKING}/:orderShipmentId?`, flags)}
              component={ShipmentTracking}
              key="12"
            />
            <Route
              path={addPathPrefix(MY_ACCOUNT, flags)}
              component={MyAccountContainer}
              key="13"
            />
            <Route exact path={LOCK_IN_RATE_PATH} component={LockInRatePage} key="14" />
            <Route exact path={CONTACT_US_PATH} component={ContactUs} key="15" />
            <Route component={NotFoundError} key="16" />
          </Switch>
        </AnalyticsWrapper>
      </Router>
    </FFFErrorBoundary>
  );
};

export default withLDConsumer()(AppRoutes);
